import React from 'react';
import { useFormikContext } from 'formik';

import { useStipReport } from '../../hooks/use-stip-report';
import { useFetchOrgaos } from '../../hooks/fetch/use-fetch-orgaos';
import { useFetchServiceTypes } from '../../hooks/fetch/use-fetch-service-types';
import { useFetchContractTypes } from '../../hooks/fetch/use-fetch-contract-types';
import { useFetchFilteredLinesByCompany } from '../../hooks/fetch/use-fetch-filtered-lines-by-company';

import Loading from '../../../../layouts/Loading';
import { GridItem } from '../../components/GridItem';
import { ReportTextField } from '../../components/ReportTextField';
import { ReportDatePicker } from '../../components/ReportDatePicker';
import { ReportMultiSelect } from '../../components/ReportMultiSelect';
import { ReportSectionTitle } from '../../components/ReportSectionTitle';
import { ReportMultiAutocomplete } from '../../components/ReportMultiAutocomplete';
import {ReportRadioGroup} from "../../components/ReportRadioGroup";
import { situacoesLinha } from "./contract-report-options";

const POR_N_LINHA = 2;

const StripContractReport = () => {
  const { handleSelectChange, handleRadioChange } = useStipReport();
  const { values } = useFormikContext();

  const { loading: loadingOrgaos, orgaos } = useFetchOrgaos();
  const { loading: loadinServiceTypes, serviceTypes } = useFetchServiceTypes();
  const { loading: loadingContractTypes, contractTypes } = useFetchContractTypes();
  const { loading: loadingLines, lines, companies } = useFetchFilteredLinesByCompany({ boStrip: true, lineRequest: { boOrdenarPorNuLinha: true }});

  const loading = loadingOrgaos || loadingLines || loadinServiceTypes || loadingContractTypes;
  const reportByLineNumber = values.cdTipoRelatorioContratos === POR_N_LINHA;

  const getLabelDeRazaoSocialOrNmCompleto = option => {
    return Boolean(option.deRazaoSocial) ? option.deRazaoSocial : option.nmCompleto;
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <GridItem container spacing={3}>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Situação da linha:</ReportSectionTitle>
        <ReportRadioGroup name={'situacaoLinha'}
          options={situacoesLinha}
          onSelect={handleRadioChange}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Opções de filtro:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) empresa(s) atual(ais):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={companies}
          onSelect={handleSelectChange}
          label="Todas as empresas"
          name="empresas"
          valueProperty="cdEmpresa"
          labelProperty="deRazaoSocial"
          uppercaseLabels
          getOptionLabel={getLabelDeRazaoSocialOrNmCompleto}
          limitTags={1}
        />
      </GridItem>
      <GridItem sm={6}>
        <ReportSectionTitle gutterBottom>Selecione a(s) linha(s) / serviço(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={lines}
          disabled={lines.length === 0}
          onSelect={handleSelectChange}
          label={lines.length === 0 ? 'Não há linhas disponíveis' : 'Todos as linhas / serviços'}
          name="linhas"
          valueProperty="cdLinha"
          labelProperty="deDescricao"
          limitTags={1}
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Filtrar por data de cadastro:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data inicial" name="dtCadastro.dtInicio" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data final" name="dtCadastro.dtFim" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Filtrar por data de início da vigencia:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data inicial" name="dtVigencia.dtInicio" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data final" name="dtVigencia.dtFim" />
      </GridItem>
      <GridItem>
        <ReportSectionTitle>Filtrar por data de vencimento:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data inicial" name="dtVencimento.dtInicio" />
      </GridItem>
      <GridItem sm={3}>
        <ReportDatePicker label="Data final" name="dtVencimento.dtFim" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Outros filtros:</ReportSectionTitle>
      </GridItem>
      <GridItem sm={3}>
        <ReportSectionTitle gutterBottom>Número do documento:</ReportSectionTitle>
        <ReportTextField name="nuDocumento" label="Número do documento" />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider>Selecione o(s) órgão(s):</ReportSectionTitle>
      </GridItem>
      <GridItem sm={4}>
        <ReportMultiSelect
          options={orgaos}
          onSelect={handleSelectChange}
          label="Todos os órgãos"
          name="orgaos"
          valueProperty="cdOrgao"
          labelProperty="sgOrgao"
        />
      </GridItem>

      <GridItem>
        <ReportSectionTitle divider />
      </GridItem>

      {reportByLineNumber && (
        <>
          <GridItem sm={4}>
            <ReportSectionTitle gutterBottom>
              Selecione o(s) tipo(s) de contrato(s):
            </ReportSectionTitle>
            <ReportMultiAutocomplete
              options={contractTypes}
              onSelect={handleSelectChange}
              label="Todos os tipos de contrato"
              name="tiposContrato"
              valueProperty="cdTipoContrato"
              labelProperty="deTipoContrato"
            />
          </GridItem>
        </>
      )}

      <GridItem sm={4}>
        <ReportSectionTitle gutterBottom>Selecione o(s) tipo(s) de serviço(s):</ReportSectionTitle>
        <ReportMultiAutocomplete
          options={serviceTypes}
          onSelect={handleSelectChange}
          label="Todos os tipos de serviços"
          name="tiposServico"
          valueProperty="cdTipoServico"
          labelProperty="deTipoServico"
        />
      </GridItem>
    </GridItem>
  );
};

export default StripContractReport;
